import { signal } from '@angular/core';

export interface GpsMapPosition {
  lat: number;
  lng: number;
}

/** GPS devices report a useful bearing only while the asset is moving. */
export const GPS_HEADING_MINIMUM_SPEED_KPH = 1;

/** Converts a finite heading to the canonical map range, 0 inclusive to 360 exclusive. */
export function normalizeGpsHeading(value?: number | string | null): number | null {
  if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) {
    return null;
  }
  const heading = Number(value);
  if (!Number.isFinite(heading)) {
    return null;
  }
  return ((heading % 360) + 360) % 360;
}

export function isGpsMoving(speedKph?: number | string | null): boolean {
  const speed = Number(speedKph);
  return Number.isFinite(speed) && speed > GPS_HEADING_MINIMUM_SPEED_KPH;
}

/** The signed, shortest turn from one bearing to another, in the range [-180, 180). */
export function shortestGpsHeadingDelta(from: number, to: number): number {
  const start = normalizeGpsHeading(from) ?? 0;
  const end = normalizeGpsHeading(to) ?? start;
  return ((end - start + 540) % 360) - 180;
}

export function interpolateGpsHeading(from: number, to: number, progress: number): number {
  const boundedProgress = Math.min(Math.max(progress, 0), 1);
  return normalizeGpsHeading((normalizeGpsHeading(from) ?? 0)
    + shortestGpsHeadingDelta(from, to) * boundedProgress) ?? 0;
}

/**
 * Interpolates a GPS marker between accepted samples. The signal lets Angular
 * update map-marker bindings on every animation frame without polling.
 */
export class GpsMarkerAnimator {
  readonly position = signal<GpsMapPosition | null>(null);
  /** The rendered bearing. It is deliberately retained while a vehicle is stopped. */
  readonly headingDegrees = signal(0);

  private animationFrame?: number;
  private fallbackTimer?: ReturnType<typeof setTimeout>;
  private lastReliableHeading?: number;

  constructor(private readonly onFrame?: (position: GpsMapPosition) => void) {}

  moveTo(
    target: GpsMapPosition | null,
    headingDegrees?: number | string | null,
    speedKph?: number | string | null,
    durationMs = 700
  ): void {
    this.cancel();
    if (!target) {
      this.clear();
      return;
    }

    const end = { lat: target.lat, lng: target.lng };
    const start = this.position();
    const endHeading = this.resolveHeading(headingDegrees, speedKph);
    const startHeading = this.headingDegrees();
    if (!start || durationMs <= 0 || (this.samePosition(start, end) && startHeading === endHeading)) {
      this.setState(end, endHeading);
      return;
    }

    const startedAt = this.now();
    const duration = Math.max(80, durationMs);
    const step = (frameTime: number): void => {
      const progress = Math.min((frameTime - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.setState({
        lat: start.lat + (end.lat - start.lat) * eased,
        lng: start.lng + (end.lng - start.lng) * eased
      }, interpolateGpsHeading(startHeading, endHeading, eased));
      if (progress < 1) {
        this.requestFrame(step);
      }
    };
    this.requestFrame(step);
  }

  snapTo(
    position: GpsMapPosition | null,
    headingDegrees?: number | string | null,
    speedKph?: number | string | null
  ): void {
    this.cancel();
    if (!position) {
      this.clear();
      return;
    }
    this.setState({ lat: position.lat, lng: position.lng }, this.resolveHeading(headingDegrees, speedKph));
  }

  destroy(): void {
    this.cancel();
  }

  private setState(position: GpsMapPosition, headingDegrees: number): void {
    this.position.set(position);
    this.headingDegrees.set(headingDegrees);
    this.onFrame?.(position);
  }

  private clear(): void {
    this.position.set(null);
    this.headingDegrees.set(0);
    this.lastReliableHeading = undefined;
  }

  private resolveHeading(rawHeading?: number | string | null, speedKph?: number | string | null): number {
    const heading = normalizeGpsHeading(rawHeading);
    if (heading !== null && isGpsMoving(speedKph)) {
      this.lastReliableHeading = heading;
    }
    return this.lastReliableHeading ?? 0;
  }

  private requestFrame(callback: FrameRequestCallback): void {
    if (typeof requestAnimationFrame === 'function') {
      this.animationFrame = requestAnimationFrame(callback);
      return;
    }
    this.fallbackTimer = setTimeout(() => callback(this.now()), 16);
  }

  private cancel(): void {
    if (this.animationFrame !== undefined && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = undefined;
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = undefined;
    }
  }

  private samePosition(left: GpsMapPosition, right: GpsMapPosition): boolean {
    return Math.abs(left.lat - right.lat) < 0.0000001
      && Math.abs(left.lng - right.lng) < 0.0000001;
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }
}
